import { Box } from "@mui/material";
import { GridSortItem } from "@mui/x-data-grid";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import CustomDataGridV2 from "../../../../../../../components/CustomDataGridV2";
import { GlobalTableHeightWrapper } from "../../../../../../../components/GlobalTableHeightWrapper";
import LoadingOverlay from "../../../../../../../components/LoadingOverlay";
import Text from "../../../../../../../components/Text";
import ActionMenu from "../../../../../../../components/app/ActionMenu";
import { useRoleController } from "../../../../../../../components/useRoleController";
import { PROVIDER_PATHS } from "../../../../../../../constants/paths";
import useDisclosure from "../../../../../../../hooks/use-disclosure";
import { useGetClaimsV2 } from "../../../../../../../queries/billings/claims/use-get-claims-v2";
import { useGetEras } from "../../../../../../../queries/billings/era/use-get-eras";
import { formatDate } from "../../../../../../../utils/date-time-utility";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
  getValueFromObj,
  replaceUnderScoreAddSpace,
  roleFilter,
} from "../../../../../../../utils/utils";
import UploadEraEobModal from "../../../../billings/era-eob/UploadEraEobModal";
import ViewEraEob from "../../../../billings/era-eob/ViewEraEob";

type EraClaim = {
  uuid?: string;
  claimId?: string;
  claimStatus?: string;
  claimType?: string;
  chargeAmount?: number;
  allowedAmount?: number;
  insurancePaid?: number;
  patientResponsibility?: number;
  insuranceAdjustment?: number;
  patientProcedureCodes?: Array<Record<string, unknown>>;
};

type EraDto = {
  uuid?: string;
  eraDocNumber?: string;
  eraDate?: string;
  paymentMethod?: string;
  reconciled?: boolean;
  claims?: EraClaim[];
};

type EraEobRow = {
  uuid: string;
  eraId: string;
  claimId: string;
  transactionDate: string;
  serviceId: string;
  claimType: string;
  paymentMethod: string;
  postingStatus: string;
  totalBilledAmount: number;
  totalAllowedAmount: number;
  totalPaidAmount: number;
  totalPatientResponsibility: number;
  totalAdjustments: number;
};

const PatientEraEob = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  // const [_searchParams, setSearchParams] = useSearchParams();
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const [downloadEraUuid, setDownloadEraUuid] = useState<string>("");

  const { hasView: hasViewEraEob } = useRoleController("Generate Claims");
  const { hasUpdate: hasUpdateEraEob } = useRoleController("Generate Claims");
  const { hasUpdate: hasPostPayment } = useRoleController("Generate Claims");
  const { hasUpdate: hasWriteOffPayment } =
    useRoleController("Generate Claims");
  const { hasUpdate: hasVoidPayment } = useRoleController("Generate Claims");

  const viewEraEobDisclosure = useDisclosure();
  const postPaymentDisclosure = useDisclosure();
  const writeOffPaymentDisclosure = useDisclosure();
  const voidPaymentDisclosure = useDisclosure();
  const uploadEraEobDisclosure = useDisclosure();

  const { data: patientClaimsData } = useGetClaimsV2({
    pagination: { page: 0, pageSize: 1000 },
    gridSorting: [],
    patientUuids: patientId ? [patientId] : undefined,
    enabled: !!patientId,
  });

  const patientClaimUuidSet = useMemo(() => {
    const uuids = new Set<string>();
    (patientClaimsData?.data?.content ?? []).forEach((c: any) => {
      if (c?.uuid) uuids.add(c.uuid);
    });
    return uuids;
  }, [patientClaimsData]);

  const {
    data: eraData,
    isLoading,
    isFetching,
  } = useGetEras({
    pagination,
    gridSorting,
  });

  const allContent = (eraData?.data?.content ?? []) as EraDto[];

  const patientScopedContent = useMemo(() => {
    if (patientClaimUuidSet.size === 0) return [];
    return allContent.filter((era) =>
      (era?.claims ?? []).some((claim: any) =>
        patientClaimUuidSet.has(claim?.uuid)
      )
    );
  }, [allContent, patientClaimUuidSet]);

  const formatCurrency = (amount: number) => `$ ${amount.toFixed(2)}`;

  const rows = useMemo<EraEobRow[]>(() => {
    const sumBy = (claims: EraClaim[], key: keyof EraClaim) =>
      claims.reduce((acc, claim) => acc + Number(claim?.[key] ?? 0), 0);

    return patientScopedContent.map((era) => {
      const claims = (era?.claims ?? []).filter((c: any) =>
        patientClaimUuidSet.has(c?.uuid)
      );
      const firstClaim = claims[0];
      const firstProcedure = firstClaim?.patientProcedureCodes?.[0];
      const serviceId =
        getValueFromObj(
          ((firstProcedure as Record<string, unknown> | undefined)?.[
            "procedureCode"
          ] as Record<string, string>) ?? {}
        ) ?? "-";

      return {
        uuid: era?.uuid ?? "",
        eraId:
          era?.eraDocNumber ?? era?.uuid?.split("-")?.[0]?.toUpperCase() ?? "-",
        claimId: (firstClaim as any)?.uuid ?? "",
        transactionDate: era?.eraDate ?? "",
        serviceId,
        claimType:
          firstClaim?.claimType === "electronic_claim"
            ? "Electronic"
            : "Manual",
        paymentMethod: era?.paymentMethod ?? "-",
        postingStatus: era?.reconciled ? "Posted" : "Not Posted",
        totalBilledAmount: sumBy(claims, "chargeAmount"),
        totalAllowedAmount: sumBy(claims, "allowedAmount"),
        totalPaidAmount: sumBy(claims, "insurancePaid"),
        totalPatientResponsibility: sumBy(claims, "patientResponsibility"),
        totalAdjustments: sumBy(claims, "insuranceAdjustment"),
      };
    });
  }, [patientScopedContent, patientClaimUuidSet]);

  const COLUMNS = applyDefaultColumnSettings<EraEobRow>([
    {
      field: "eraId",
      flex: 0.5,
      minWidth: 100,
      headerName: "ERA ID",
      sortable: false,
      renderCell: ({ row }) => <Text isTabletext>{row?.eraId || "-"}</Text>,
    },
    {
      field: "claimId",
      flex: 0.5,
      minWidth: 100,
      headerName: "Claim ID",
      sortable: false,
      renderCell: ({ row }) => <Text isTabletext>{row?.claimId || "-"}</Text>,
    },
    {
      field: "transactionDate",
      flex: 0.6,
      minWidth: 140,
      headerName: "Transaction Date",
      sortable: false,
      renderCell: ({ row }) => (
        <Text>{formatDate(row?.transactionDate) ?? "-"}</Text>
      ),
    },
    {
      field: "serviceId",
      flex: 1,
      minWidth: 200,
      headerName: "Service ID",
      sortable: false,
      renderCell: ({ row }) => (
        <Text
          isTabletext
          title={row?.serviceId || "-"}
          sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
        >
          {row?.serviceId || "-"}
        </Text>
      ),
    },
    {
      field: "claimType",
      flex: 0.5,
      minWidth: 120,
      headerName: "Claim Type",
      sortable: false,
      renderCell: ({ row }) => (
        <Text isTabletext>
          {replaceUnderScoreAddSpace(row?.claimType?.toLowerCase()) || "-"}
        </Text>
      ),
    },
    {
      field: "paymentMethod",
      flex: 0.5,
      minWidth: 130,
      headerName: "Payment Method",
      sortable: false,
      renderCell: ({ row }) => (
        <Text isTabletext>
          {replaceUnderScoreAddSpace(row?.paymentMethod) || "-"}
        </Text>
      ),
    },
    {
      field: "postingStatus",
      flex: 0.6,
      minWidth: 130,
      headerName: "Posting Status",
      sortable: false,
      renderCell: ({ row }) => (
        <Text isTabletext>
          {replaceUnderScoreAddSpace(row?.postingStatus) || "-"}
        </Text>
      ),
    },
    {
      field: "totalBilledAmount",
      flex: 0.6,
      minWidth: 150,
      headerName: "Total Billed amount",
      sortable: false,
      renderCell: ({ row }) => (
        <Text isTabletext>{formatCurrency(row?.totalBilledAmount || 0)}</Text>
      ),
    },
    {
      field: "totalAllowedAmount",
      flex: 0.6,
      minWidth: 150,
      headerName: "Total Allowed amount",
      sortable: false,
      renderCell: ({ row }) => (
        <Text isTabletext>{formatCurrency(row?.totalAllowedAmount || 0)}</Text>
      ),
    },
    {
      field: "totalPaidAmount",
      flex: 0.6,
      minWidth: 150,
      headerName: "Total Paid amount",
      sortable: false,
      renderCell: ({ row }) => (
        <Text isTabletext>{formatCurrency(row?.totalPaidAmount || 0)}</Text>
      ),
    },
    {
      field: "totalPatientResponsibility",
      flex: 0.7,
      minWidth: 160,
      headerName: "Total Patient Respor",
      sortable: false,
      renderCell: ({ row }) => (
        <Text isTabletext>
          {formatCurrency(row?.totalPatientResponsibility || 0)}
        </Text>
      ),
    },
    {
      field: "totalAdjustments",
      flex: 0.6,
      minWidth: 150,
      headerName: "Total Adjustments",
      sortable: false,
      renderCell: ({ row }) => (
        <Text isTabletext>{formatCurrency(row?.totalAdjustments || 0)}</Text>
      ),
    },
    {
      field: "Action",
      width: 90,
      hideSortIcons: true,
      renderCell: ({ row }) => {
        return (
          <ActionMenu
            options={roleFilter({
              list: [
                { value: "view_era_eob", label: "View ERA/EOB" },
                { value: "view_claim", label: "View Claim" },
                { value: "post_payment", label: "Post Payment" },
                { value: "download_era", label: "Download ERA" },
                {
                  value: "write_off_payment",
                  label: "Write off Payment Post",
                },
                { value: "void_payment", label: "Void Payment Post" },
                { value: "upload_era_eob", label: "Upload ERA/EOB" },
              ],
              compWith: {
                view_era_eob: hasViewEraEob,
                view_claim: hasViewEraEob,
                post_payment: hasPostPayment,
                download_era: hasViewEraEob,
                write_off_payment: hasWriteOffPayment,
                void_payment: hasVoidPayment,
                upload_era_eob: hasUpdateEraEob,
              },
            })}
            onActionClick={(v: string) => {
              if (v === "view_era_eob") {
                viewEraEobDisclosure.open(row.uuid);
              } else if (v === "view_claim") {
                navigate(
                  PROVIDER_PATHS.BILLING +
                    `claims/edit-claim?claimId=${row?.claimId}&readOnly=true`
                );
                // navigate(PROVIDER_PATHS.CLAIMS + "claims/edit-claim");
                // setSearchParams({
                //   claimId: row?.claimId,
                //   readOnly: "true",
                // });
              } else if (v === "post_payment") {
                postPaymentDisclosure.open(row.uuid);
              } else if (v === "download_era") {
                setDownloadEraUuid(row.uuid);
              } else if (v === "write_off_payment") {
                writeOffPaymentDisclosure.open(row.uuid);
              } else if (v === "void_payment") {
                voidPaymentDisclosure.open(row.uuid);
              } else if (v === "upload_era_eob") {
                uploadEraEobDisclosure.open();
              }
            }}
          />
        );
      },
    },
  ]);

  void postPaymentDisclosure;
  void writeOffPaymentDisclosure;
  void voidPaymentDisclosure;

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <LoadingOverlay isLoading={!!downloadEraUuid} />

      <UploadEraEobModal disclosureHost={uploadEraEobDisclosure} />

      <ViewEraEob
        disclosureHost={viewEraEobDisclosure}
        eraEobUuid={viewEraEobDisclosure.data}
      />
      <ViewEraEob
        disclosureHost={{
          opened: false,
          open: () => undefined,
          close: () => undefined,
          data: undefined,
        }}
        eraEobUuid={
          downloadEraUuid
            ? {
                uuid: downloadEraUuid,
                autoDownload: true,
              }
            : undefined
        }
        onAutoDownloadComplete={() => setDownloadEraUuid("")}
      />

      <GlobalTableHeightWrapper marginTop={0}>
        <CustomDataGridV2
          loading={isLoading || isFetching}
          rows={rows}
          getRowId={(row: EraEobRow) => row?.uuid}
          columns={COLUMNS}
          onPaginationModelChange={setPagination}
          onSortModelChange={setGridSorting}
          paginationModel={pagination}
          rowCount={rows.length}
          sortModel={gridSorting}
        />
      </GlobalTableHeightWrapper>
    </Box>
  );
};

export default PatientEraEob;
